import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import api, { LOGO_URL } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { Seo } from "@/components/Seo";
import { SERVICES } from "@/data/services";
import { SERVICE_AREAS } from "@/lib/seo";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/a5d72c5ebd074ba597adce65938d97a08eb94fc8ca88edccb5e0e646ee7efb8e.jpeg";
const MANIFESTO_IMG = "https://static.prod-images.emergentagent.com/jobs/184f6891-74ff-4ba6-8bec-c6519cbaf0d7/images/aa5ebe8bd66669d43ce5e0074a932dae91db171f30ab188ca815e3f6d04e812e.jpeg";

const chapters = [
  {
    n: "01",
    title: "The Material",
    body: "Walnut, slate, brass, steel, full-grain leather under the laser — glass and acrylic under the UV printer. We only work with materials that deserve a permanent mark.",
  },
  {
    n: "02",
    title: "The Beam",
    body: "The xTool F2 Ultra carries two lasers — a 60W MOPA fiber for metal and a 40W diode for organics — resolving detail finer than a human hair at 0.001mm precision.",
  },
  {
    n: "03",
    title: "Full Colour",
    body: "Our UV flatbed printer cures ink instantly under ultraviolet light, bonding photographic colour directly onto acrylic, wood, metal and glass — and prints UV DTF transfers you can apply to almost any surface.",
  },
  {
    n: "04",
    title: "Yours Alone",
    body: "A name, a date, a map of the place you met. Every piece leaves the studio carrying something that belongs to exactly one person on earth.",
  },
];

const specs = [
  ["FIBER LASER", "60W · MOPA"],
  ["DIODE LASER", "40W"],
  ["PRECISION", "0.001 MM"],
  ["UV PRINT", "CMYK + WHITE + GLOSS"],
  ["UV DTF", "TRANSFERS · ANY SURFACE"],
  ["MATERIALS", "METAL · WOOD · LEATHER · SLATE · ACRYLIC · GLASS (UV)"],
  ["WORK AREA", "220 × 220 MM"],
  ["TURNAROUND", "5–7 BUSINESS DAYS"],
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title="Custom Laser Engraving & Corporate Gifts in Leduc, Alberta | Ember & Steel Studio"
        description="Ember & Steel Studio: personalised laser engraving, custom-branded drinkware, premium corporate gifts, metal business cards and UV printing in Leduc, Alberta — serving Edmonton, Nisku, Beaumont and area. Shipping across Canada and to the USA."
        path="/"
        jsonLd={[{ "@context": "https://schema.org", "@type": "WebSite", name: "Ember & Steel Studio", url: "https://embersteelstudio.ca", inLanguage: "en-CA" }]}
      />
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] flex items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-[#050505]" />
        </motion.div>
        <div className="absolute inset-0 spotlight" />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-24 w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-12 bg-[#C87455]" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">
              Laser Engraving · UV Printing
            </span>
          </motion.div>

          <h1 className="font-serif-display font-light text-5xl sm:text-7xl lg:text-[6.5rem] leading-[1.02] tracking-tight text-white copper-glow">
            <MaskedLine delay={0.35}>Marked by fire.</MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-[#C87455] italic">Finished</span> in steel.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-xl text-zinc-400 text-base md:text-lg leading-relaxed"
          >
            Personalised pieces engraved and printed in-house — one at a time —
            on wood, metal, glass, slate and leather.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-btn"
              className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm flex items-center gap-3"
            >
              Shop the Collection <ArrowRight size={14} />
            </Link>
            <Link
              to="/quote"
              data-testid="hero-quote-btn"
              className="border border-[#A9B0B7]/50 text-[#A9B0B7] hover:bg-[#A9B0B7] hover:text-black transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm"
            >
              Commission Custom Work
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 right-8 lg:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-zinc-500"
        >
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-rl]">Scroll</span>
          <ArrowDown size={14} className="animate-bounce" />
        </motion.div>
      </section>

      <Marquee />

      {/* POSITIONING + SERVICE PATHWAYS */}
      <section className="py-24 md:py-28 border-b border-zinc-900" data-testid="home-positioning">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Leduc · Edmonton · Alberta</span>
              <h2 className="font-serif-display font-light text-3xl md:text-5xl text-white mt-4 tracking-tight leading-tight">
                Custom laser engraving &amp; corporate gifts in Leduc, Alberta
              </h2>
              <p className="text-zinc-400 mt-6 leading-relaxed">
                Ember &amp; Steel Studio specialises in personalised laser engraving, custom-branded drinkware, premium corporate gifts,
                custom metal products and UV printing. We serve {SERVICE_AREAS.slice(0, 4).join(", ")} and surrounding Alberta communities,
                with custom solutions for individuals and businesses — and ship finished work across Canada.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/services" data-testid="home-services-link" className="link-line font-mono-tech text-xs uppercase tracking-[0.2em] text-[#C87455]">All services →</Link>
                <Link to="/faq" data-testid="home-faq-link" className="link-line font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white">Common questions →</Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}>
                <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`} className="group block bg-[#050505] hover:bg-[#0B0B0B] transition-colors duration-500 p-6 h-full">
                  <div className="flex items-center justify-between">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-zinc-600">0{i + 1}</span>
                    <ArrowRight size={13} className="text-zinc-700 group-hover:text-[#C87455] group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-serif-display text-xl md:text-2xl text-white mt-4">{s.h1}</h3>
                  <p className="text-zinc-500 text-xs leading-relaxed mt-2 line-clamp-2">{s.materials.slice(0, 3).join(" · ")}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-16 gap-6">
            <Reveal>
              <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">The Collection</span>
              <h2 className="font-serif-display font-light text-3xl md:text-5xl text-white mt-4 tracking-tight">
                Made to be kept
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="hidden sm:block shrink-0">
              <Link to="/shop" data-testid="featured-view-all-link" className="link-line font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors">
                View all pieces →
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.slice(0, 6).map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 md:py-32 border-t border-zinc-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">Manifesto</span>
                <h2 className="font-serif-display font-light text-3xl md:text-5xl text-white mt-4 tracking-tight leading-tight">
                  Nothing leaves the studio<br />
                  <span className="italic text-[#A9B0B7]">unmarked.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-12 relative overflow-hidden">
                  <img src={MANIFESTO_IMG} alt="The workshop" className="w-full aspect-[4/3] object-cover opacity-70" />
                  <div className="absolute inset-0 border border-zinc-800" />
                  <img src={LOGO_URL} alt="" className="absolute bottom-4 right-4 h-16 w-16 opacity-90" />
                </div>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-0">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="group border-t border-zinc-900 py-12 grid grid-cols-12 gap-6 hover:bg-white/[0.015] transition-colors duration-500" data-testid={`manifesto-chapter-${c.n}`}>
                  <span className="col-span-2 font-mono-tech text-sm text-[#C87455]/70">{c.n}</span>
                  <div className="col-span-10">
                    <h3 className="font-serif-display text-2xl md:text-3xl text-white mb-4">{c.title}</h3>
                    <p className="text-zinc-400 leading-relaxed max-w-lg">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MACHINE SPECS */}
      <section className="border-t border-zinc-900 bg-[#080808] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-4 mb-12">
              <span className="h-px w-12 bg-[#C87455]" />
              <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-zinc-500">
                Instruments · xTool F2 Ultra + UV Flatbed
              </span>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {specs.map(([k, v], i) => (
              <Reveal key={k} delay={i * 0.05}>
                <div className="bg-[#080808] p-8 h-full">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-3">{k}</p>
                  <p className="font-mono-tech text-sm md:text-base text-[#A9B0B7]">{v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 spotlight" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <Reveal>
            <h2 className="font-serif-display font-light text-4xl md:text-6xl text-white tracking-tight leading-tight">
              Have something that deserves
              <span className="italic text-[#C87455]"> a permanent mark?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-zinc-400 mt-6 max-w-xl mx-auto">
              Bring us your idea — a logo, a photograph, a phrase — and we'll tell you exactly
              what it looks like burned into wood, steel or glass.
            </p>
          </Reveal>
          <Reveal delay={0.35}>
            <Link
              to="/quote"
              data-testid="cta-quote-btn"
              className="inline-flex items-center gap-3 mt-10 bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-10 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm"
            >
              Request a Quote <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
